import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { fetchGateState, gateStateQueryKey } from "@/lib/gate-state";

export const ROLE_PICKER_PATH = "/signup/choose-role";

/**
 * Makes choosing a logistics role MANDATORY before any dashboard or onboarding
 * wizard renders.
 *
 * Why this exists (2026-08-12): picking a role is what starts the 14-day trial
 * — the vernot trigger `companies_auto_trial_window` stamps trial_started_at /
 * trial_ends_at on the UPDATE that sets `logistics_role`, and it deliberately
 * waits for a role because putting a CARRIER on a trial would cap their
 * permanent unlimited $0 tier at 100 credits. So an account that skips the
 * picker gets no clock at all: `check_usage_limit` resolves it to the pre-plan
 * `dispatcher-trial` bucket whose allowance is 0, and the user reads
 * "0 / 0 credits" from their first second. 98 real accounts were stranded that
 * way before this gate — the routes leaked because both `chooseLandingPath` and
 * `HomeGate` sent a role-less user to `/dash/onboarding`, the DISPATCHER
 * wizard, which never sets a role.
 *
 * Behaviour:
 *   - resolved company with a role     → render children
 *   - resolved company with no role    → redirect to the role picker
 *   - no company row at all            → redirect to the role picker; the
 *     picker's `setup-account` fallback creates the row when the client-side
 *     update matches nothing
 *   - QA / test tenants                → exempt, same carve-out HomeGate makes
 *     for onboarding (overnight QA TOP ISSUE 2)
 *   - read failed                      → FAIL OPEN. A transient network error
 *     must never wall a paying customer out of their own dashboard, and
 *     HomeGate still gates the index route underneath.
 *
 * The picker lives at a top-level route OUTSIDE this wrapper, so there is no
 * redirect loop. SignupRolePicker invalidates this query key on success —
 * without that, the 10s staleTime would serve role=null right after selection
 * and bounce the user straight back here.
 */
export function RequireLogisticsRole({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: gateStateQueryKey(user?.id),
    queryFn: () => fetchGateState(user!.id),
    enabled: !!user?.id,
    staleTime: 10_000,
    retry: 1,
  });

  // Unauthenticated is ProtectedRoute's job, not ours.
  if (!user?.id) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  // Fail open — see the note above.
  if (isError) return <>{children}</>;

  const company = data?.company ?? null;
  if (company?.is_test_tenant) return <>{children}</>;
  if (!company?.logistics_role) return <Navigate to={ROLE_PICKER_PATH} replace />;

  return <>{children}</>;
}
